/*
 * Spotta lit concrete wall — shared by the landing page and every page hero.
 * A procedural tilt-up concrete wall is generated once on the GPU, an optional
 * logo is "painted" onto it, then a single downlight is relit whenever it moves.
 * Falls back to plain CSS when WebGL is unavailable.
 *
 * <section data-spotta-wall data-intro="session|flicker|quick|none" data-lazy="true"
 *          data-light-y="-30" data-kelvin data-intensity data-wear data-paint data-dust data-pan>
 */
(() => {
  'use strict';

  const PRECISION = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
`;

  const VERT = 'attribute vec2 a;void main(){gl_Position=vec4(a,0.,1.);}';

  const NOISE = `
uniform float u_seed;
vec2 hash22(vec2 p){vec3 p3=fract(vec3(p.xyx)*vec3(.1031,.1030,.0973));p3+=dot(p3,p3.yzx+33.33);return fract((p3.xx+p3.yz)*p3.zy);}
float hash12(vec2 p){vec3 p3=fract(vec3(p.xyx)*.1031);p3+=dot(p3,p3.yzx+33.33);return fract((p3.x+p3.y)*p3.z);}
float gnoise(vec2 p){
  vec2 i=floor(p),f=fract(p);vec2 u=f*f*f*(f*(f*6.-15.)+10.);
  float a=dot(hash22(i+u_seed)*2.-1.,f);
  float b=dot(hash22(i+vec2(1.,0.)+u_seed)*2.-1.,f-vec2(1.,0.));
  float c=dot(hash22(i+vec2(0.,1.)+u_seed)*2.-1.,f-vec2(0.,1.));
  float d=dot(hash22(i+vec2(1.,1.)+u_seed)*2.-1.,f-vec2(1.,1.));
  return mix(mix(a,b,u.x),mix(c,d,u.x),u.y);
}
const mat2 ROT=mat2(.8,-.6,.6,.8);
float fbm3(vec2 p){float s=0.,a=.5;for(int i=0;i<3;i++){s+=a*gnoise(p);p=ROT*p*2.03+17.1;a*=.5;}return s;}
float fbm4(vec2 p){float s=0.,a=.5;for(int i=0;i<4;i++){s+=a*gnoise(p);p=ROT*p*2.01+31.7;a*=.5;}return s;}
`;

  // Pass 1: concrete wall -> RGBA(albedo*2, normal.x, normal.y, occlusion)
  const GEN_WALL = PRECISION + NOISE + `
uniform vec2 u_res;uniform float u_scale;
const float PW=360.;
float pores(vec2 p,float cell,float prob,float rmin,float rmax,float depth){
  vec2 q=p/cell;vec2 i=floor(q),f=fract(q);float h=0.;
  for(int y=-1;y<=1;y++)for(int x=-1;x<=1;x++){
    vec2 g=vec2(float(x),float(y));vec2 id=i+g+u_seed;
    float r=hash12(id*1.37+4.1);
    if(r<prob){
      vec2 d=(g+hash22(id)-f)*cell;
      float rad=mix(rmin,rmax,hash12(id+9.3));
      float t=clamp(length(d)/rad,0.,1.);
      h=min(h,-depth*rad*sqrt(1.-t*t));
    }
  }
  return h;
}
float jointDist(vec2 p){float xl=mod(p.x+PW*.5,PW);return min(xl,PW-xl);}
float poreField(vec2 p){return pores(p,2.6,.075,.06,.22,.75)+pores(p,9.,.085,.2,.6,.7);}
float H(vec2 p){
  float h=fbm3(p/48.)*.22+fbm4(p/7.5)*.085+fbm3(p/2.3)*.03+fbm3(p/1.3+7.)*.03+gnoise(p/.9)*.012+gnoise(p/.45)*.005;
  h+=poreField(p);
  float d=jointDist(p);
  h-=.55*(1.-smoothstep(.85,1.7,d));
  return h;
}
float albedo(vec2 p,float pore){
  float a=.19+fbm4(p/30.)*.11+fbm3(p/7.)*.045+(1.-abs(fbm3(p/16.+5.)*2.))*.03+smoothstep(.12,.42,fbm3(p/12.+3.))*.04+gnoise(p/.8)*.016;
  float pid=floor((p.x+PW*.5)/PW);
  a+=(hash12(vec2(pid,3.7)+u_seed)-.5)*.035;
  a+=fbm3(vec2(p.x/2.6,p.y/150.))*.03*smoothstep(10.,120.,p.y);
  vec2 pc=vec2(mod(p.x+PW*.5,PW)-PW*.5,p.y);
  for(int k=0;k<2;k++){
    vec2 c=vec2(k==0?-96.:96.,k==0?58.:236.);
    float dc=length(pc-c)+gnoise(p/1.4)*.5;
    a+=.014*(1.-smoothstep(3.5,6.5,dc));
  }
  float d=jointDist(p);
  a=mix(a*.9,.085,1.-smoothstep(.8,.95,d));
  return a*(1.+pore*2.2);
}
void main(){
  vec2 px=vec2(gl_FragCoord.x,u_res.y-gl_FragCoord.y);
  vec2 p=vec2(px.x-u_res.x*.5,px.y)*u_scale;
  float e=u_scale;
  float h=H(p),hx=H(p+vec2(e,0.)),hy=H(p+vec2(0.,e));
  vec3 n=normalize(vec3(-(hx-h)/e,-(hy-h)/e,1.));
  float pore=poreField(p);
  float d=jointDist(p);
  float ao=clamp(1.+pore*1.8-.35*(1.-smoothstep(.9,2.2,d)),.2,1.);
  gl_FragColor=vec4(clamp(albedo(p,pore)*2.,0.,1.),n.x*.5+.5,n.y*.5+.5,ao);
}`;

  // Pass 2: painted logo coverage -> R coverage, G paint tone
  const GEN_PAINT = PRECISION + NOISE + `
uniform vec2 u_res;uniform float u_scale;uniform sampler2D u_logo;uniform vec4 u_rect;uniform float u_wear;
vec2 vor(vec2 x,out float cid){
  vec2 n=floor(x),f=fract(x);float F1=8.,F2=8.;cid=0.;
  for(int j=-1;j<=1;j++)for(int i=-1;i<=1;i++){
    vec2 g=vec2(float(i),float(j));vec2 o=hash22(n+g+u_seed*3.1);
    vec2 r=g+o-f;float d=dot(r,r);
    if(d<F1){F2=F1;F1=d;cid=hash12(n+g+11.7);}else if(d<F2){F2=d;}
  }
  return sqrt(vec2(F1,F2));
}
void main(){
  vec2 px=vec2(gl_FragCoord.x,u_res.y-gl_FragCoord.y);
  vec2 lu=(px-u_rect.xy)/u_rect.zw;
  if(lu.x<0.||lu.y<0.||lu.x>1.||lu.y>1.){gl_FragColor=vec4(0.);return;}
  vec2 p=vec2(px.x-u_res.x*.5,px.y)*u_scale;
  float m=texture2D(u_logo,lu).a;
  float edge=(gnoise(p/.55)*.6+gnoise(p/.18)*.4)*.42*u_wear;
  float cov=smoothstep(.34,.66,m+edge);
  vec2 w=vec2(gnoise(p/6.),gnoise(p/6.+9.1))*.55;
  float c1,c2;
  vec2 a=vor(p/6.8+w,c1);
  vec2 b=vor(p/2.4+w*1.6,c2);
  float wide=.03+.05*smoothstep(-.3,.45,gnoise(p/19.));
  float crackA=1.-smoothstep(wide,wide+.035,a.y-a.x);
  float dense=smoothstep(-.05,.35,fbm3(p/14.+4.));
  float crackB=(1.-smoothstep(.05,.1,b.y-b.x))*dense;
  float chip=step(.955,c2)*smoothstep(.1,.35,dense);
  float wear=u_wear*(.55+.45*smoothstep(-.25,.35,fbm3(p/22.)));
  cov*=1.-clamp(max(max(crackA,crackB),chip),0.,1.)*wear;
  float tone=.86+.1*fbm3(p/3.2)+.04*gnoise(vec2(p.x/9.,p.y/.8));
  gl_FragColor=vec4(cov,clamp(tone,0.,1.),0.,1.);
}`;

  // Pass 3: relight every frame
  const LIGHT = PRECISION + `
uniform sampler2D u_wall;uniform sampler2D u_paintTex;
uniform vec2 u_res;uniform float u_scale;uniform float u_time;
uniform vec3 u_lpos;uniform vec3 u_ldir;uniform vec3 u_lcol;uniform float u_level;
uniform vec3 u_paint;uniform vec3 u_amb;uniform float u_cosIn;uniform float u_cosOut;uniform float u_haze;
float h12(vec2 p){vec3 p3=fract(vec3(p.xyx)*.1031);p3+=dot(p3,p3.yzx+33.33);return fract((p3.x+p3.y)*p3.z);}
vec3 aces(vec3 x){return clamp((x*(2.51*x+.03))/(x*(2.43*x+.59)+.14),0.,1.);}
void main(){
  vec2 uv=gl_FragCoord.xy/u_res;
  vec4 w=texture2D(u_wall,uv);
  vec4 pt=texture2D(u_paintTex,uv);
  float paint=pt.r;
  vec2 px=vec2(gl_FragCoord.x,u_res.y-gl_FragCoord.y);
  vec3 P=vec3((px.x-u_res.x*.5)*u_scale,px.y*u_scale,0.);
  vec3 n=vec3(w.g*2.-1.,w.b*2.-1.,0.);n.z=sqrt(max(0.,1.-dot(n.xy,n.xy)));
  n=normalize(mix(n,vec3(0.,0.,1.),paint*.6));
  float ao=mix(w.a,1.,paint*.75);
  vec3 alb=mix(vec3(w.r*.5)*vec3(.97,.99,1.03),u_paint*pt.g,paint);
  vec3 L=u_lpos-P;float r2=dot(L,L);float r=sqrt(r2);vec3 l=L/r;
  float ct=dot(-l,u_ldir);
  float spot=smoothstep(u_cosOut,u_cosIn,ct);
  spot=spot*spot*(1.15-.15*spot);
  float spill=.032*smoothstep(.1,.74,ct);
  float I=1.7e4*u_level;
  float ndl=max(dot(n,l),0.);
  vec3 E=u_lcol*I*(spot+spill)*ndl/r2;
  vec3 cam=vec3(0.,u_res.y*u_scale*.42,380.);
  vec3 v=normalize(cam-P);vec3 hv=normalize(l+v);
  float rough=mix(.5,.36,paint);float a2=rough*rough*rough*rough;
  float sh=2./a2-2.;
  float spec=pow(max(dot(n,hv),0.),sh)*(sh+8.)/25.13*mix(.045,.07,paint);
  vec3 col=alb*(E+u_amb)*ao+u_lcol*spec*I*(spot+spill)*ndl/r2;
  vec3 Q=vec3(P.xy,u_lpos.z*.6);vec3 dq=Q-u_lpos;float dl=length(dq)+1.;
  float hz=smoothstep(.86,.985,dot(dq/dl,u_ldir))*smoothstep(u_lpos.y,u_lpos.y+12.,P.y)/(1.+dl*dl/2600.);
  col+=u_lcol*hz*u_haze*u_level;
  vec2 q=(uv-vec2(.5,.55))*vec2(u_res.x/u_res.y*.62,1.);
  col*=1.-.42*smoothstep(.35,1.05,length(q));
  col=aces(col*1.25);
  col=pow(col,vec3(1./2.2));
  col+=(h12(gl_FragCoord.xy+fract(u_time)*61.)-.5)/255.;
  gl_FragColor=vec4(col,1.);
}`;

  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  function kelvinToRGB(k) {
    const t = k / 100;
    let r, g, b;
    if (t <= 66) {
      r = 255;
      g = 99.4708025861 * Math.log(t) - 161.1195681661;
      b = t <= 19 ? 0 : 138.5177312231 * Math.log(t - 10) - 305.0447927307;
    } else {
      r = 329.698727446 * Math.pow(t - 60, -0.1332047592);
      g = 288.1221695283 * Math.pow(t - 60, -0.0755148492);
      b = 255;
    }
    const rgb = [r, g, b].map((c) => clamp(c, 0, 255) / 255);
    const m = Math.max(...rgb);
    return rgb.map((c) => 0.2 + 0.8 * (c / m));
  }

  function hexToLinear(hex) {
    const h = (hex || '#e9e4da').replace('#', '');
    const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => Math.pow(c / 255, 2.2));
  }

  // Lamp start-up: two quick flickers, then a smooth warm-up.
  function introLevel(t) {
    const keys = [[0, 0], [160, 0], [190, 0.55], [240, 0.06], [300, 0.72], [350, 0.18], [420, 0.4]];
    if (t <= 0) return 0;
    for (let i = 1; i < keys.length; i++) {
      if (t < keys[i][0]) {
        const [t0, v0] = keys[i - 1];
        const [t1, v1] = keys[i];
        return v0 + (v1 - v0) * ((t - t0) / (t1 - t0));
      }
    }
    return 0.4 + 0.6 * easeOutCubic(clamp((t - 420) / 1100, 0, 1));
  }

  class Motes {
    constructor(canvas) {
      this.c = canvas;
      this.ctx = canvas.getContext('2d');
      this.p = [];
      this.sprite = document.createElement('canvas');
      const s = (this.sprite.width = this.sprite.height = 32);
      const g = this.sprite.getContext('2d');
      const grd = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
      grd.addColorStop(0, 'rgba(255,238,215,1)');
      grd.addColorStop(0.35, 'rgba(255,225,190,.45)');
      grd.addColorStop(1, 'rgba(255,220,180,0)');
      g.fillStyle = grd;
      g.fillRect(0, 0, s, s);
    }
    resize(w, h, dpr) {
      this.w = w; this.h = h; this.dpr = dpr;
      this.c.width = Math.round(w * dpr);
      this.c.height = Math.round(h * dpr);
      const n = w < 750 ? 34 : 64;
      this.p = Array.from({ length: n }, () => this.spawn(true));
    }
    spawn(anywhere) {
      return {
        x: Math.random(),
        y: anywhere ? Math.random() : 1.05,
        vx: (Math.random() - 0.5) * 0.004,
        vy: -(0.002 + Math.random() * 0.006),
        s: 0.5 + Math.pow(Math.random(), 2.5) * 1.8,
        ph: Math.random() * Math.PI * 2,
        tw: 0.4 + Math.random() * 1.2,
      };
    }
    draw(dt, t, level, lx, ly, pan) {
      const { ctx, w, h, dpr } = this;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      if (level <= 0.01) return;
      ctx.globalCompositeOperation = 'lighter';
      const ax = Math.sin(pan), ay = Math.cos(pan);
      const k = dt / 16.7;
      for (const m of this.p) {
        m.vx += (Math.random() - 0.5) * 0.0006 * k;
        m.vy += (Math.random() - 0.5) * 0.0006 * k;
        m.vx *= 0.985; m.vy *= 0.985;
        m.x += m.vx * 0.05 * k; m.y += m.vy * 0.05 * k;
        if (m.y < -0.05 || m.x < -0.05 || m.x > 1.05 || m.y > 1.1) Object.assign(m, this.spawn(false));
        const X = m.x * w, Y = m.y * h;
        const dx = X - lx, dy = Y - ly;
        if (dy <= 4) continue;
        const d = Math.hypot(dx, dy);
        const c = (dx * ax + dy * ay) / d;
        const cone = clamp((c - 0.86) / 0.11, 0, 1);
        if (cone <= 0) continue;
        const fall = 1 / (1 + (d * d) / (h * h * 0.18));
        const tw = 0.55 + 0.45 * Math.sin(m.ph + t * 0.001 * m.tw);
        const a = level * cone * cone * fall * tw * 0.9;
        if (a < 0.02) continue;
        const r = m.s * 2.6;
        ctx.globalAlpha = a;
        ctx.drawImage(this.sprite, X - r, Y - r, r * 2, r * 2);
      }
      ctx.globalAlpha = 1;
    }
  }

  class SpottaWall {
    constructor(el) {
      this.el = el;
      this.fixture = el.querySelector('[data-fixture]');
      this.glow = this.fixture || el.querySelector('[data-glow]');
      this.logo = el.querySelector('[data-logo]');
      this.doors = [...el.querySelectorAll('[data-door]')];
      this.glCanvas = el.querySelector('[data-gl]');
      this.moteCanvas = el.querySelector('[data-motes]');
      const d = el.dataset;
      this.cfg = {
        kelvin: +d.kelvin || 3000,
        intensity: +d.intensity || 1,
        wear: d.wear != null ? +d.wear : 0.8,
        paint: d.paint || '#e9e4da',
        motes: d.dust !== 'false',
        pan: d.pan !== 'false',
        seed: +d.seed || 3.7,
        intro: d.intro || 'session',
        lazy: d.lazy === 'true',
        lightY: d.lightY != null ? +d.lightY : -30,
      };
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.design = !!(window.Shopify && window.Shopify.designMode);
      this.level = 0;
      this.pan = 0;
      this.panTarget = 0;
      this.cursor = 0;
      this.raf = 0;
      this.mode = 'intro';
      this.visible = true;
      this.disposers = [];
      this.el.classList.add('is-js');
      if (this.cfg.lazy && 'IntersectionObserver' in window) {
        const lazy = new IntersectionObserver((es) => {
          if (es.some((e) => e.isIntersecting)) { lazy.disconnect(); this.init(); }
        }, { rootMargin: '240px 0px' });
        lazy.observe(el);
        this.disposers.push(() => lazy.disconnect());
      } else {
        this.init();
      }
    }

    on(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      this.disposers.push(() => target.removeEventListener(type, fn, opts));
    }

    init() {
      let ok = false;
      try { ok = this.setupGL(); } catch (e) { ok = false; }
      this.el.classList.toggle('is-gl', ok);
      this.el.classList.toggle('no-gl', !ok);
      if (this.cfg.motes && !this.reduced && this.moteCanvas) this.motes = new Motes(this.moteCanvas);

      this.measure();
      if (ok) this.generate();

      const seen = (() => { try { return sessionStorage.getItem('spotta-lit') === '1'; } catch (e) { return false; } })();
      try { sessionStorage.setItem('spotta-lit', '1'); } catch (e) { /* private mode */ }
      // 'session': lamp flickers on for the first page of a visit, then a quick fade.
      // 'flicker' always flickers (used for lamps you scroll down to). None for reduced motion / the editor.
      const want = this.cfg.intro;
      this.intro = this.reduced || this.design || want === 'none' ? 'none'
        : want === 'flicker' ? 'full' : want === 'quick' ? 'quick' : seen ? 'quick' : 'full';
      if (this.intro === 'none') { this.mode = 'idle'; this.level = 1; }
      this.t0 = performance.now() + (this.intro === 'full' ? 250 : 60);
      const revealAt = { none: 0, quick: 180, full: 1050 }[this.intro];
      this.revealTimer = setTimeout(() => this.el.classList.add('is-revealed'), revealAt);

      this.bindEvents();
      if ('IntersectionObserver' in window) {
        const vis = new IntersectionObserver((es) => {
          this.visible = es[es.length - 1].isIntersecting;
          if (this.visible) this.kick();
        });
        vis.observe(this.el);
        this.disposers.push(() => vis.disconnect());
      }
      this.kick();
    }

    setupGL() {
      const c = this.glCanvas;
      if (!c) return false;
      const gl = c.getContext('webgl', { antialias: false, alpha: false, depth: false, stencil: false, premultipliedAlpha: false, powerPreference: 'high-performance' });
      if (!gl) return false;
      this.gl = gl;
      const mk = (fs) => {
        const p = gl.createProgram();
        const sh = (type, src) => {
          const s = gl.createShader(type);
          gl.shaderSource(s, src);
          gl.compileShader(s);
          if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
          gl.attachShader(p, s);
        };
        sh(gl.VERTEX_SHADER, VERT);
        sh(gl.FRAGMENT_SHADER, fs);
        gl.bindAttribLocation(p, 0, 'a');
        gl.linkProgram(p);
        if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p));
        const u = {};
        const n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
        for (let i = 0; i < n; i++) { const info = gl.getActiveUniform(p, i); u[info.name] = gl.getUniformLocation(p, info.name); }
        return { p, u };
      };
      this.progWall = mk(GEN_WALL);
      this.progPaint = mk(GEN_PAINT);
      this.progLight = mk(LIGHT);
      const buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      gl.enableVertexAttribArray(0);
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
      this.on(c, 'webglcontextlost', (e) => { e.preventDefault(); this.fallback(); });
      return true;
    }

    fallback() {
      this.gl = null;
      this.el.classList.remove('is-gl');
      this.el.classList.add('no-gl');
    }

    tex(w, h, data) {
      const gl = this.gl;
      const t = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      if (data) gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, data);
      else gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      return t;
    }

    measure() {
      const r = this.el.getBoundingClientRect();
      this.W = Math.max(1, r.width);
      this.H = Math.max(1, r.height);
      // Cap the backing store at ~2.4 MP so big retina screens and laptop GPUs stay at 60fps.
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.dpr = Math.min(dpr, Math.sqrt(2.4e6 / (this.W * this.H)));
      // Wall scale follows the viewport, so the concrete looks the same size on every page.
      this.cmPerCss = 280 / Math.max(window.innerHeight || this.H, this.W * 0.5);
      const lens = this.el.querySelector('[data-lens]');
      this.lightY = lens ? lens.getBoundingClientRect().bottom - r.top : this.cfg.lightY;
      if (this.motes) this.motes.resize(this.W, this.H, Math.min(window.devicePixelRatio || 1, 1.25));
      const lb = this.logo ? this.logo.getBoundingClientRect() : { left: 0, top: 0, width: 0 };
      this.logoKey = [lb.left - r.left, lb.top - r.top, lb.width, this.W, this.H].map(Math.round).join(',');
      this.doorCenters = this.doors.map((d) => { const b = d.getBoundingClientRect(); return [b.left + b.width / 2 - r.left, b.top + b.height / 2 - r.top]; });
    }

    drawLogoMask() {
      const svg = this.logo && this.logo.querySelector('svg');
      if (!svg) return null;
      const lr = this.logo.getBoundingClientRect();
      const sr = this.el.getBoundingClientRect();
      const vb = (svg.getAttribute('viewBox') || '0 0 100 100').split(/[\s,]+/).map(Number);
      const s = this.dpr;
      const w = Math.max(2, Math.round(lr.width * s));
      const h = Math.max(2, Math.round((lr.width * vb[3] / vb[2]) * s));
      const cv = document.createElement('canvas');
      cv.width = w; cv.height = h;
      const ctx = cv.getContext('2d');
      ctx.setTransform(w / vb[2], 0, 0, h / vb[3], -vb[0] * w / vb[2], -vb[1] * h / vb[3]);
      ctx.fillStyle = '#fff';
      svg.querySelectorAll('path').forEach((p) => ctx.fill(new Path2D(p.getAttribute('d')), p.getAttribute('fill-rule') === 'nonzero' ? 'nonzero' : 'evenodd'));
      return { canvas: cv, rect: [(lr.left - sr.left) * s, (lr.top - sr.top) * s, w, h] };
    }

    generate() {
      const gl = this.gl;
      if (!gl) return;
      gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, null);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, null);
      const w = Math.round(this.W * this.dpr), h = Math.round(this.H * this.dpr);
      this.glCanvas.width = w; this.glCanvas.height = h;
      this.pw = w; this.ph = h;
      this.scale = this.cmPerCss / this.dpr; // cm per device px
      [this.tWall, this.tPaint, this.tLogo].forEach((t) => t && gl.deleteTexture(t));
      (this.fbos || []).forEach((f) => gl.deleteFramebuffer(f));
      this.tWall = this.tex(w, h);
      this.tPaint = this.tex(w, h);
      const fbWall = gl.createFramebuffer(), fbPaint = gl.createFramebuffer();
      this.fbos = [fbWall, fbPaint];
      gl.viewport(0, 0, w, h);

      gl.bindFramebuffer(gl.FRAMEBUFFER, fbWall);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.tWall, 0);
      let pr = this.progWall;
      gl.useProgram(pr.p);
      gl.uniform2f(pr.u.u_res, w, h);
      gl.uniform1f(pr.u.u_scale, this.scale);
      gl.uniform1f(pr.u.u_seed, this.cfg.seed);
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      const mask = this.drawLogoMask();
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbPaint);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.tPaint, 0);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      if (mask) {
        this.tLogo = this.tex(0, 0, mask.canvas);
        gl.bindTexture(gl.TEXTURE_2D, this.tLogo);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        pr = this.progPaint;
        gl.useProgram(pr.p);
        gl.uniform2f(pr.u.u_res, w, h);
        gl.uniform1f(pr.u.u_scale, this.scale);
        gl.uniform1f(pr.u.u_seed, this.cfg.seed);
        gl.uniform1f(pr.u.u_wear, this.cfg.wear);
        gl.uniform4f(pr.u.u_rect, ...mask.rect);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.tLogo);
        gl.uniform1i(pr.u.u_logo, 0);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
      }
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      this.lcol = kelvinToRGB(this.cfg.kelvin);
      this.paintLin = hexToLinear(this.cfg.paint);
      this.dirty = true;
    }

    render(now) {
      const gl = this.gl;
      if (!gl || !this.tWall) return;
      const pr = this.progLight;
      gl.viewport(0, 0, this.pw, this.ph);
      gl.useProgram(pr.p);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, this.tWall); gl.uniform1i(pr.u.u_wall, 0);
      gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, this.tPaint); gl.uniform1i(pr.u.u_paintTex, 1);
      const cm = this.cmPerCss;
      const a = this.pan;
      gl.uniform2f(pr.u.u_res, this.pw, this.ph);
      gl.uniform1f(pr.u.u_scale, this.scale);
      gl.uniform1f(pr.u.u_time, now * 0.001);
      gl.uniform3f(pr.u.u_lpos, Math.sin(a) * 6, this.lightY * cm, 34);
      const dir = [Math.sin(a), Math.cos(a), -0.2];
      const dl = Math.hypot(...dir);
      gl.uniform3f(pr.u.u_ldir, dir[0] / dl, dir[1] / dl, dir[2] / dl);
      gl.uniform3f(pr.u.u_lcol, ...this.lcol);
      gl.uniform1f(pr.u.u_level, this.level * this.cfg.intensity);
      gl.uniform3f(pr.u.u_paint, ...this.paintLin);
      gl.uniform3f(pr.u.u_amb, 0.012, 0.0135, 0.017);
      gl.uniform1f(pr.u.u_cosIn, Math.cos(0.30));
      gl.uniform1f(pr.u.u_cosOut, Math.cos(0.70));
      gl.uniform1f(pr.u.u_haze, 0.009);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    bindEvents() {
      this.doors.forEach((door, i) => {
        const aim = () => { if (this.cfg.pan && this.mode !== 'leave') this.aimAt(i); };
        const rest = () => { if (this.mode !== 'leave') { this.panTarget = 0; this.kick(); } };
        this.on(door, 'pointerenter', aim);
        this.on(door, 'focus', aim);
        this.on(door, 'pointerleave', rest);
        this.on(door, 'blur', rest);
        this.on(door, 'click', (e) => this.leave(e, door, i));
      });
      this.on(this.el, 'pointermove', (e) => {
        if (e.pointerType !== 'mouse' || this.reduced || !this.cfg.pan) return;
        this.cursor = ((e.clientX - this.el.getBoundingClientRect().left) / this.W - 0.5) * 0.06;
        this.kick();
      });
      let rt;
      const ro = new ResizeObserver(() => {
        clearTimeout(rt);
        rt = setTimeout(() => {
          const r = this.el.getBoundingClientRect();
          if (Math.abs(r.width - this.W) < 1 && Math.abs(r.height - this.H) < 1) return;
          this.measure(); this.generate(); this.kick();
        }, 160);
      });
      ro.observe(this.el);
      this.disposers.push(() => ro.disconnect());
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          const before = this.logoKey;
          this.measure();
          if (this.logoKey !== before || this.el.classList.contains('no-gl')) { if (this.gl) this.generate(); }
          this.kick();
        });
      }
      this.on(document, 'visibilitychange', () => { if (!document.hidden) this.kick(); });
      this.on(window, 'pageshow', (e) => {
        if (!e.persisted) return;
        this.mode = 'idle'; this.level = 1; this.panTarget = 0;
        this.el.classList.remove('is-leaving');
        this.el.classList.add('is-revealed');
        this.kick();
      });
    }

    aimAt(i) {
      const c = this.doorCenters[i];
      if (!c) return;
      const dx = c[0] - this.W / 2, dy = Math.max(40, c[1] - this.lightY);
      this.panTarget = Math.abs(dx) < 24 ? 0 : clamp(Math.atan2(dx, dy) * 0.9, -0.5, 0.5);
      this.kick();
    }

    leave(e, door, i) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (door.target === '_blank' || this.reduced || this.design) return;
      const href = door.href;
      if (!href) return;
      e.preventDefault();
      this.aimAt(i);
      this.mode = 'leave';
      this.leaveStart = performance.now();
      this.el.classList.add('is-leaving');
      door.classList.add('is-chosen');
      this.kick();
      setTimeout(() => { window.location.href = href; }, 520);
    }

    kick() {
      if (!this.raf) { this.last = performance.now(); this.raf = requestAnimationFrame((t) => this.frame(t)); }
    }

    frame(now) {
      this.raf = 0;
      if (document.hidden || !this.visible) return;
      const dt = Math.min(64, now - this.last);
      this.last = now;
      let busy = false;
      if (this.mode === 'intro') {
        const t = now - this.t0;
        const end = this.intro === 'full' ? 1560 : 700;
        this.level = this.intro === 'full' ? introLevel(t) : easeOutCubic(clamp(t / 700, 0, 1));
        if (t > end) { this.mode = 'idle'; this.level = 1; } else busy = true;
      } else if (this.mode === 'leave') {
        const t = (now - this.leaveStart) / 480;
        this.level = 1 - 0.85 * easeOutCubic(clamp(t, 0, 1));
        busy = t < 1;
      } else {
        this.level = 1;
      }
      const target = this.panTarget + (this.mode === 'leave' ? 0 : this.cursor);
      const k = 1 - Math.exp(-dt / (this.mode === 'leave' ? 140 : 260));
      this.pan += (target - this.pan) * k;
      if (Math.abs(target - this.pan) > 0.0004) busy = true;
      if (this.glow) {
        this.glow.style.setProperty('--light', this.level.toFixed(3));
        this.glow.style.setProperty('--pan', this.pan.toFixed(4));
      }
      // Only relight the wall when something changed; the dust has its own cheap canvas.
      if (this.dirty || this.level !== this.drawnLevel || Math.abs(this.pan - this.drawnPan) > 1e-5) {
        this.render(now);
        this.dirty = false; this.drawnLevel = this.level; this.drawnPan = this.pan;
      }
      if (this.motes) {
        this.motes.draw(dt, now, this.level, this.W / 2 + Math.sin(this.pan) * 6 / this.cmPerCss, this.lightY, this.pan);
        busy = true;
      }
      if (busy) this.raf = requestAnimationFrame((t) => this.frame(t));
    }

    destroy() {
      cancelAnimationFrame(this.raf);
      clearTimeout(this.revealTimer);
      this.disposers.forEach((d) => d());
      if (this.gl) { const ext = this.gl.getExtension('WEBGL_lose_context'); if (ext) ext.loseContext(); }
    }
  }

  const instances = new Map();
  const mount = (root) => {
    (root || document).querySelectorAll('[data-spotta-wall]').forEach((el) => {
      if (instances.has(el)) return;
      instances.set(el, new SpottaWall(el));
    });
  };
  const unmount = (root) => {
    instances.forEach((inst, el) => { if (!root || root.contains(el)) { inst.destroy(); instances.delete(el); } });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount());
  else mount();

  document.addEventListener('shopify:section:load', (e) => mount(e.target));
  document.addEventListener('shopify:section:unload', (e) => unmount(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    instances.forEach((inst, el) => {
      const i = inst.doors.indexOf(e.target);
      if (el.contains(e.target) && i > -1) inst.aimAt(i);
    });
  });
  document.addEventListener('shopify:block:deselect', () => instances.forEach((inst) => { inst.panTarget = 0; inst.kick(); }));
})();
